import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import PropTypes from 'prop-types'
import {
  getZoneColor,
  getZoneName,
  getStrokeString,
  formatSeconds,
  zoneList,
  strokeList
} from './Utils'
import Modal from 'react-modal'
import MapRouteComponent from './MapRouteComponent'
import SafeMarkdownComponent from './SafeMarkdownComponent'
import DetailSectionComponent, { DetailMore } from './Shared/DetailSectionComponent'
import DetailFieldComponent, { DetailGrid } from './Shared/DetailFieldComponent'
import DetailJumpNavComponent from './Shared/DetailJumpNavComponent'
import DetailStatComponent from './Shared/DetailStatComponent'
import RawFieldsComponent from './Shared/RawFieldsComponent'
import {
  EMPTY,
  describeRawValue,
  formatBool,
  formatDate,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatSwimmerLocalTime,
  formatUtcOffset,
  isBlank,
  joinSummary,
  rawFieldsSummary
} from './Shared/detailFormat'
import HeartRateChartsComponent from './CompletedWorkout/HeartRateChartsComponent'
import LapTableComponent, { lapStrokeCounts } from './CompletedWorkout/LapTableComponent'
import {
  byStrokeRows,
  describePool,
  describePoolCourse,
  hasGpsTrack,
  isYardsWorkout
} from './CompletedWorkout/completedWorkoutFields'

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)'
  }
}

// In page order. Summary, heart rate and sets are open on load; the rest are
// collapsed and the jump opens them.
const SECTIONS = [
  { id: 'cw-summary', label: 'Summary' },
  { id: 'cw-heart-rate', label: 'Heart rate' },
  { id: 'cw-sets', label: 'Sets' },
  { id: 'cw-context', label: 'When & where' },
  { id: 'cw-device', label: 'Source & device' },
  { id: 'cw-record', label: 'Workout record' },
  { id: 'cw-feedback', label: 'Feedback & coach' },
  { id: 'cw-laps', label: 'Laps' },
  { id: 'cw-integrations', label: 'Files & integrations' },
  { id: 'cw-raw', label: 'All fields' }
]

// CompletedWorkout_v3 fields the structured sections render. Anything else
// stored on the row is flagged in the "All fields" section.
const COMPLETED_WORKOUT_SHOWN_FIELDS = [
  'title', 'user', 'workout', 'dateCompleted', 'recordedStartDate',
  'secondsFromGMT', 'totalDistance', 'totalTime', 'calories', 'pool',
  'poolCourse', 'workoutType', 'comments', 'rpeScore', 'syncUUID',
  'workoutHash_v1', 'healthKitUUID', 'isGuided', 'isGenerated',
  'isSplashModeRunner', 'workoutSource', 'isSourceGarmin',
  'workoutRunnerPlatform', 'modelName', 'deviceVersion', 'appVersion',
  'heartRateData', 'hrSummary_v1', 'lapSamplesData', 'locationSamples',
  'startLocation', 'averageWaterTemperatureInCelsius',
  'swolfFreestyleAverage_v5', 'avgFreePacePer100m_v2',
  'shouldLogToExternalServices', 'garminActivityId', 'garminConnectWorkoutId',
  'stravaActivityId', 'stravaShareImageUrl', 'stravaHealAttempted',
  'sourceFITFile', 'createdFITFile', 'attachmentSyncUUIDs',
  'attachmentSyncTypes', 'image', 'isDisqualifiedFromChallenges',
  // Legacy, shown under "Legacy metrics"
  'swolfFreestyleAverage', 'swolfFreestyleAverage_v1',
  'swolfFreestyleAverage_v2', 'swolfFreestyleAverage_v3',
  'swolfFreestyleAverage_v4', 'avgFreePacePer100m_v1', 'stravaShareImage',
  'socialObject', 'recordedEndDate', 'fitFileData', 'isAdminWorkout',
  // Social
  'likeArray', 'commentArray'
]

const WORKOUT_SHOWN_FIELDS = [
  'title', 'setGroups', 'totalDistance', 'totalTime', 'pool', 'poolCourse',
  'poolLength', 'workoutType', 'skillLevel', 'isAllStrength',
  'isTestSetWorkout', 'isAdminWorkout', 'isPremium', 'isFree', 'comments',
  'recordedStartDate', 'recordedEndDate', 'trainingPlanId',
  'trainingPlanEntryId', 'workoutOfTheDayId', 'authorUser', 'lastEditUser',
  'totalDistanceByStroke', 'totalDurationByStroke', 'isGenerated', 'aiModel',
  'generatedDate', 'userId', 'requestedDistance', 'requestedStrokes',
  'requestedEquipment', 'requestedFocus', 'imageUrl', 'imageID', 'lastCaller',
  'workoutHash_v1', 'trainingPeaksWorkoutId', 'subtitle'
]

const COACH_FEEDBACK_SHOWN_FIELDS = [
  'completedWorkoutId', 'text', 'improvementOpportunity', 'actionPlan',
  'systemPrompt', 'userPrompt', 'isExpired'
]

const MEMBER_FEEDBACK_SHOWN_FIELDS = [
  'completedWorkoutId', 'userId', 'workoutLength', 'workoutInterval',
  'starRating', 'recommendationSeen', 'recommendationSeenTimestamp', 'notes'
]

// Fields nothing writes any more, or superseded versions of a current one.
const LEGACY_FIELDS = [
  ['swolfFreestyleAverage_v4', 'SWOLF v4'],
  ['swolfFreestyleAverage_v3', 'SWOLF v3'],
  ['swolfFreestyleAverage_v2', 'SWOLF v2'],
  ['swolfFreestyleAverage_v1', 'SWOLF v1'],
  ['swolfFreestyleAverage', 'SWOLF (unversioned)'],
  ['avgFreePacePer100m_v1', 'Free pace /100m v1'],
  ['stravaShareImage', 'Strava share image (file)'],
  ['socialObject', 'Social object'],
  ['recordedEndDate', 'Recorded end'],
  ['fitFileData', 'FIT file data'],
  ['isAdminWorkout', 'Admin workout']
]

// workoutType on CompletedWorkout_v3 and Workout_v3. Not the stroke-category
// enum in Utils.getWorkoutTypeString.
const WORKOUT_TYPE_LABELS = {
  0: 'Technique',
  1: 'Endurance',
  2: 'Speed',
  3: 'Mixed'
}

const describeWorkoutType = (value) =>
  value === undefined || value === null
    ? null
    : `${WORKOUT_TYPE_LABELS[value] || 'Unknown'} (${value})`

// Set_v3 interval/goal-time modification state.
const MODIFICATION_STATE_LABELS = {
  0: 'none',
  1: 'personalized',
  2: 'edited by member'
}

// hrSummary_v1.reason when the samples couldn't be summarised.
const HR_UNUSABLE_REASONS = {
  no_samples: 'No samples',
  no_timestamps: 'Samples have no timestamps',
  failed: 'Summary failed'
}

// averageWaterTemperatureInCelsius: null means the attachment was checked and
// had nothing usable; absent means it hasn't been checked yet.
const describeWaterTemperature = (completedWorkout) => {
  // Parse's has() is false for a stored null, so check the key itself.
  if (!Object.prototype.hasOwnProperty.call(completedWorkout.attributes, 'averageWaterTemperatureInCelsius')) {
    return EMPTY
  }
  const value = completedWorkout.get('averageWaterTemperatureInCelsius')
  return value === null ? 'none usable' : formatNumber(value, 1)
}

const joinList = (values) =>
  Array.isArray(values) && values.length > 0 ? values.join(', ') : null

export default class CompletedWorkout extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      completedWorkout: null,
      activeLap: null,
      activeLapIndex: null,
      activeSet: null,
      activeSetIndex: null,
      activeSetGroupIndex: null,
      modalIsOpen: false,
      intervalMin: 0,
      intervalMs: 0,
      splits: [],
      // null while loading, undefined if the lookup failed.
      attachmentTypes: null,
      isRePushingToStrava: false,
      stravaRePushResult: null,
      // null while loading, undefined if the lookup failed.
      coachFeedback: null,
      memberFeedback: null,
      coachConversations: null,
      scheduledWorkouts: null
    }
  }

  componentDidMount () {
    if (!this.getId()) {
      return
    }
    this.loadAllExerciseTypes()
    this.loadWorkout()
    this.loadCoachFeedback()
    this.loadRelated()
  }

  getId = () => this.props.id || this.props.match?.params?.id

  loadWorkout = async () => {
    const id = this.getId()
    try {
      const query = new Parse.Query('CompletedWorkout_v3')
      query.include(['workout.setGroups.sets', 'image', 'user.userInfo_v3'])
      const completedWorkout = await query.get(id)
      this.setState({ completedWorkout })
      this.loadAttachmentTypes(completedWorkout)
    } catch (error) {
      alert('Error getting completed workout ' + id + ': ' + error.message)
    }
  }

  // Also run for workouts that declared nothing: an undeclared upload still
  // belongs on this page.
  loadAttachmentTypes = async (completedWorkout) => {
    this.setState({ attachmentTypes: null })
    try {
      const params = {
        userId: completedWorkout.get('user').id,
        completedWorkoutSyncUUID: completedWorkout.get('syncUUID')
      }
      const results = await Parse.Cloud.run('getCompletedWorkoutAttachments', params)
      const attachments = results.map(attachment => ({
        syncUUID: attachment.syncUUID,
        type: attachment.attachmentType,
        extension: attachment.attachmentFileExtension,
        createdAt: attachment.createdAt,
        url: attachment.attachmentUrl
      }))
      this.setState({
        attachmentTypes: attachments
      })
    } catch (error) {
      console.error('Error loading attachment types: ' + error.message)
      this.setState({ attachmentTypes: undefined })
    }
  }

  // Reads the stored feedback rather than calling getWorkoutCoachFeedback,
  // which generates (and saves) feedback for the member when none exists yet.
  // Opening this page must not create member-facing AI output.
  loadCoachFeedback = async () => {
    try {
      const query = new Parse.Query('WorkoutCoachFeedback')
      query.equalTo('completedWorkoutId', this.getId())
      query.descending('createdAt')
      const results = await query.find()
      this.setState({ coachFeedback: results })
    } catch (error) {
      console.error('Error loading coach feedback: ' + error.message)
      this.setState({ coachFeedback: undefined })
    }
  }

  // Rows in other classes that hang off this workout. Each loads on its own so
  // one failing lookup only blanks its own panel.
  loadRelated = () => {
    const id = this.getId()
    const pointer = Parse.Object.extend('CompletedWorkout_v3').createWithoutData(id)
    this.loadInto('memberFeedback', 'CompletedWorkoutFeedback', (query) =>
      query.equalTo('completedWorkoutId', id))
    this.loadInto('coachConversations', 'SwimCoachConversation', (query) => {
      query.equalTo('entityType', 'completedWorkout')
      query.equalTo('entityId', id)
    })
    this.loadInto('scheduledWorkouts', 'ScheduledWorkout', (query) =>
      query.equalTo('completedWorkout', pointer))
  }

  loadInto = async (stateKey, className, constrain) => {
    try {
      const query = new Parse.Query(className)
      constrain(query)
      query.descending('createdAt')
      query.limit(100)
      const results = await query.find()
      this.setState({ [stateKey]: results })
    } catch (error) {
      console.error(`Error loading ${className}: ` + error.message)
      this.setState({ [stateKey]: undefined })
    }
  }

  loadAllExerciseTypes = async () => {
    try {
      const query = new Parse.Query('DrylandExercise')
      query.limit(1000)
      const results = await query.find()
      this.setState({ exerciseTypes: results })
    } catch (error) {
      alert(error)
    }
  }

  handleDisqualify = async (e) => {
    e.preventDefault()
    const completedWorkout = this.state.completedWorkout
    const isCurrentlyDisqualified = !!completedWorkout.get(
      'isDisqualifiedFromChallenges'
    )
    completedWorkout.set(
      'isDisqualifiedFromChallenges',
      !isCurrentlyDisqualified
    )
    try {
      await completedWorkout.save()
    } catch (error) {
      window.alert('Error saving: ' + error.message)
    }
    this.setState({
      completedWorkout: completedWorkout
    })
  }

  getExerciseString = (value) => {
    if (value === undefined || !this.state.exerciseTypes) {
      return 'undefined'
    }
    if (!this.state.exerciseTypes[value]) {
      return 'undefined'
    }
    return this.state.exerciseTypes[value].get('name')
  }

  handleEditUser = (event) => {
    event.preventDefault()

    const newId = window.prompt('Please enter the new userId')
    if (!newId || newId === '') return

    const user = new Parse.User()
    user.id = newId

    this.state.completedWorkout.set('user', user)
    this.state.completedWorkout.save().then(
      () => {
        // Reload so the member name follows the new owner.
        this.loadWorkout()
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleEditDistance = (event) => {
    event.preventDefault()

    const newDistance = window.prompt('Please enter the new distance')
    if (!newDistance || newDistance === '') return

    const dist = parseInt(newDistance)

    this.state.completedWorkout.set('totalDistance', dist)
    this.state.completedWorkout.save().then(
      () => {
        this.setState({
          completedWorkout: this.state.completedWorkout
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleGenerateFitFile = (event) => {
    event.preventDefault()
    const params = { completedWorkoutId: this.state.completedWorkout.id }
    Parse.Cloud.run('generateFitFile', params).then(
      (results) => {
        console.log(results)
        window.location.href = results.url
      },
      (error) => {
        window.alert('Error generating fit file: ' + error.message)
      }
    )
  }

  // Support tooling: force a single swim back onto Strava after its original
  // upload was lost. Every duplicate-protection gate (already synced, external
  // logging disabled, upload still in flight) lives in the cloud function and
  // comes back as a support-facing message, so show that message verbatim rather
  // than pre-judging the outcome here.
  handleRePushToStrava = async (event) => {
    event.preventDefault()
    const confirm = window.confirm(
      'Re-push this workout to the member\'s Strava? This posts a real activity to their account.'
    )
    if (!confirm) {
      return
    }
    this.setState({ isRePushingToStrava: true, stravaRePushResult: null })
    try {
      const params = { completedWorkoutId: this.state.completedWorkout.id }
      const result = await Parse.Cloud.run('adminRePushWorkoutToStrava', params)
      this.setState({ stravaRePushResult: result })
      if (result && result.success) {
        // Reload so the newly written stravaActivityId shows above.
        this.loadWorkout()
      }
    } catch (error) {
      this.setState({
        stravaRePushResult: {
          success: false,
          message: 'Re-push failed: ' + error.message
        }
      })
    } finally {
      this.setState({ isRePushingToStrava: false })
    }
  }

  onSetChange = (event) => {
    event.preventDefault()

    const set = this.state.activeSet
    set.set(event.target.name, parseInt(event.target.value))
    this.setState({
      activeSet: set
    })
  }

  onSelectChange = (name, event) => {
    event.preventDefault()
    const set = this.state.activeSet
    set.set(name, parseInt(event.target.value))
    this.setState({
      activeSet: set
    })
  }

  handleEditLap = (lap, index) => {
    // Edit a copy so cancelling the modal leaves the stored lap untouched.
    this.setState({
      modalIsOpen: true,
      activeLap: { ...lap },
      activeLapIndex: index
    })
  }

  handleEditSet = (set, index, groupIndex) => {
    const reps = set.get('recordedSwimTimes')
    const arr = []
    reps &&
      reps.length > 0 &&
      reps.forEach((element) => {
        const obj = {}
        obj.min = parseInt(
          (element - (Math.round(element) % 60)) / 60
        )
        obj.ms = Math.round(element) % 60
        arr.push(obj)
      })
    this.setState({
      activeSet: set,
      activeSetIndex: index,
      activeSetGroupIndex: groupIndex,
      splits: arr,
      intervalMs: set.get('interval') % 60,
      intervalMin:
        (set.get('interval') - Math.round(set.get('interval') % 60)) /
        60
    })
  }

  renderSetGroup = (setGroup, index) => {
    const sets = setGroup.get('sets') || []
    return (
      <div key={setGroup.id || index} className="mb-3">
        <h6 className="mb-1">
          {setGroup.get('title') || <span className="text-muted">Untitled set group</span>}{' '}
          <small className="text-muted">{setGroup.get('reps')}x</small>
        </h6>
        {sets.map((set, j) => this.renderSet(set, j, index))}
      </div>
    )
  }

  renderSplitList = (values) =>
    '[' + (values || [])
      .map((split) => (split ? formatSeconds(split) : 'ø'))
      .join(', ') + ']'

  // Per-set data beyond reps/distance/interval: what was planned around it and
  // what the runner recorded.
  renderSetDetails = (set) => {
    const details = [
      ['Notes', set.get('notes')],
      ['Equipment', joinList(set.get('equipment'))],
      ['Effort variation', set.get('effortVariation')],
      ['Test set', set.get('isTestSet') ? (set.get('testSetTypeKey') || 'yes') : null],
      ['Rep start times', set.get('recordedRepStartDates')?.length
        ? `${set.get('recordedRepStartDates').length} recorded`
        : null],
      ['Interval', set.get('intervalModificationState')
        ? MODIFICATION_STATE_LABELS[set.get('intervalModificationState')] || set.get('intervalModificationState')
        : null],
      ['Goal times', set.get('goalTimesModificationState')
        ? MODIFICATION_STATE_LABELS[set.get('goalTimesModificationState')] || set.get('goalTimesModificationState')
        : null],
      ['Video', set.get('video')?.id],
      ['Media', set.get('mediaURL')]
    ].filter(([, value]) => !isBlank(value))
    if (details.length === 0) {
      return null
    }
    return (
      <div className="small text-muted">
        {details.map(([label, value]) => (
          <span key={label} className="me-3">{label}: {value}</span>
        ))}
      </div>
    )
  }

  renderSet = (set, index, groupIndex) => {
    if (
      index === this.state.activeSetIndex &&
      this.state.activeSetGroupIndex === groupIndex
    ) {
      return this.renderSetEditor(set)
    }
    if (!set.get('setType')) {
      // Swim set
      return (
        <div key={set.id || index} className="detail-set">
          <b>
            {set.get('reps')} x {set.get('distance')}{' '}
            {getStrokeString(set.get('stroke'))}{' '}
            {this.renderInterval(set.get('interval'))}{' '}
          </b>
          <span
            className="detail-zone-chip"
            style={{ backgroundColor: getZoneColor(set.get('zone')) }}
          >
            {getZoneName(set.get('zone'))}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm py-0"
            onClick={() => this.handleEditSet(set, index, groupIndex)}
          >
            Edit
          </button>
          <div className="small">
            Goal times:{' '}
            <span className="text-muted">{this.renderSplitList(set.get('goalTimes'))}</span>
          </div>
          <div className="small">
            Recorded splits:{' '}
            <span className="text-muted">{this.renderSplitList(set.get('recordedSwimTimes'))}</span>
          </div>
          {this.renderSetDetails(set)}
        </div>
      )
    }
    // Dryland set
    return (
      <div key={set.id || index} className="detail-set">
        {set.get('reps')} x {this.getExerciseString(set.get('exerciseType'))}{' '}
        {this.renderInterval(set.get('interval'))}
        <div className="small text-muted">
          {this.renderSplitList(set.get('recordedSwimTimes'))}
        </div>
        {this.renderSetDetails(set)}
      </div>
    )
  }

  renderSetEditor = (set) => {
    const { activeSet } = this.state
    return (
      <div key={set.id} className="detail-set">
        <b>
          <input
            name="reps"
            value={activeSet.get('reps')}
            onChange={this.onSetChange}
            style={{ width: 50 }}
          />{' '}
          x{' '}
          <input
            name="distance"
            value={activeSet.get('distance')}
            onChange={this.onSetChange}
            style={{ width: 50 }}
          />{' '}
          {getStrokeString(set.get('stroke'))}
          <input
            className="ms-2"
            name="interval"
            value={this.state.intervalMin}
            onChange={(event) => {
              this.setState({
                intervalMin: event.target.value
              })
            }}
            style={{ width: 50 }}
          />{' '}
          :{' '}
          <input
            name="interval"
            className="ms-2"
            value={this.state.intervalMs}
            onChange={(event) => {
              this.setState({
                intervalMs: event.target.value
              })
            }}
            style={{ width: 50 }}
          />
        </b>
        &nbsp;
        <div
          style={{
            paddingLeft: 2 + 'px',
            paddingRight: 2 + 'px',
            backgroundColor: getZoneColor(set.get('zone')),
            display: 'inline-block'
          }}
        >
          {' '}
          <select
            className="form-select"
            value={activeSet.get('zone')}
            name="zone"
            onChange={(value) => this.onSelectChange('zone', value)}
          >
            {zoneList.map((item) => {
              return <option value={item.value} key={item.value}>{item.label}</option>
            })}
          </select>
        </div>
        <small
          className="btn btn-success btn-sm ms-2"
          onClick={this.updateWorkoutSet}
        >
          Save
        </small>
        <small
          className="btn btn-secondary btn-sm ms-2"
          onClick={this.clearTemp}
        >
          <span className="text-danger">Cancel</span>
        </small>
        <br />
        <div style={{ marginLeft: 20 + 'px' }} className="text-muted">
          {activeSet && activeSet.get('recordedSwimTimes') && activeSet.get('recordedSwimTimes').length > 0 && (<small>Splits</small>)}

          {(activeSet.get('recordedSwimTimes') || []).map((split, k) => {
            if (!split) return null
            return (
              <div
                className="row"
                key={k}>
                <input
                  name="interval"
                  className="me-2"
                  value={this.state.splits[k]?.min}
                  onChange={(event) => {
                    const updates = this.state.splits
                    updates[k].min = event.target.value
                    this.setState({
                      splits: updates
                    })
                  }}
                  style={{ width: 50 }}
                />
                {'  '}:{' '}
                <input
                  name="interval"
                  className="ms-2"
                  value={this.state.splits[k]?.ms}
                  onChange={(event) => {
                    const updates = this.state.splits
                    updates[k].ms = event.target.value
                    this.setState({
                      splits: updates
                    })
                  }}
                  style={{ width: 50 }}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  renderInterval = (interval) => {
    if (!interval) return ''
    return '@ ' + formatSeconds(interval)
  }

  closeModal = () => {
    this.setState({
      modalIsOpen: false,
      activeLap: null,
      activeLapIndex: null
    })
  }

  handleSaveLap = async (e) => {
    e.preventDefault()

    const completedWorkout = this.state.completedWorkout
    const lapsList = [...(completedWorkout.get('lapSamplesData') || [])]
    const lap = this.state.activeLap
    lapsList[this.state.activeLapIndex] = {
      ...lap,
      strokeType: parseInt(lap.strokeType),
      totalSwimTime: parseFloat(lap.totalSwimTime),
      strokeCount: parseInt(lap.strokeCount)
    }

    completedWorkout.set('lapSamplesData', lapsList)
    try {
      await completedWorkout.save()
    } catch (error) {
      window.alert('Error saving lap: ' + error.message)
      return
    }
    this.setState({
      completedWorkout: completedWorkout
    })
    this.closeModal()
  }

  updateWorkoutSet = async (e) => {
    e.preventDefault()

    const completedWorkout = this.state.completedWorkout
    const workout = completedWorkout.get('workout')
    const setGroups = completedWorkout.get('workout').get('setGroups')

    this.state.activeSet.set(
      'interval',
      parseInt(
        parseFloat(this.state.intervalMin * 60) +
        parseInt(this.state.intervalMs)
      )
    )

    const arr = []
    this.state.splits.forEach((element) => {
      arr.push(parseFloat(parseInt(element.min * 60) + parseInt(element.ms)))
    })

    this.state.activeSet.set('recordedSwimTimes', arr)

    const collectionOfSets =
      setGroups[this.state.activeSetGroupIndex].get('sets')
    collectionOfSets[this.state.activeSetIndex] = this.state.activeSet
    setGroups[this.state.activeSetGroupIndex].set('sets', collectionOfSets)

    workout.set('setGroups', setGroups)
    completedWorkout.set('workout', workout)
    await completedWorkout.save()
    this.setState({
      completedWorkout: completedWorkout,
      activeSet: null,
      activeSetIndex: null,
      intervalMin: 0,
      intervalMs: 0,
      splits: []
    })
  }

  clearTemp = () => {
    this.setState({
      activeSet: null,
      activeSetIndex: null,
      intervalMin: 0,
      intervalMs: 0,
      splits: []
    })
  }

  // Ad-hoc question to the AI coach. The answer is shown here only; it is not
  // saved as the member's feedback.
  generateAnalysis = async (e) => {
    e.preventDefault()
    const aiPrompt = window.prompt('Enter a prompt for AI Swim Coach. (optional)\n\nExamples:\n"What was my fastest split?"\n"What should I work on for next time?"\n"What was my average stroke count during the 100s free?"\n"What are the top three takeaways from this swim?"')
    if (aiPrompt === null) return
    this.setState({
      isGeneratingAnalysis: true,
      adHocAnalysis: null
    })
    const params = {
      completedWorkoutId: this.state.completedWorkout.id,
      prompt: aiPrompt
    }
    try {
      const result = await Parse.Cloud.run('getAIWorkoutAnalysis', params)
      this.setState({
        isGeneratingAnalysis: false,
        adHocAnalysis: {
          text: result.response,
          systemPrompt: result.systemPrompt,
          userPrompt: result.userPrompt
        }
      })
    } catch (err) {
      alert('Error generating analysis: ' + err.message)
      this.setState({
        isGeneratingAnalysis: false
      })
    }
  }

  onLapChange = (event) => {
    this.setState({
      activeLap: { ...this.state.activeLap, [event.target.name]: event.target.value }
    })
  }

  // --- Rendering helpers ---

  renderStat = (label, value, unit, source, action) => (
    <DetailStatComponent key={label} label={label} value={value} unit={unit}
      source={source} action={action} />
  )

  renderLoadState = (value, emptyText) => {
    if (value === null) return <p className="text-muted mb-0">Loading...</p>
    if (value === undefined) return <p className="text-danger mb-0">Couldn&apos;t load</p>
    if (value.length === 0) return <p className="text-muted mb-0">{emptyText}</p>
    return null
  }

  renderBadges = () => {
    const cw = this.state.completedWorkout
    const workout = cw.get('workout')
    const badges = [
      [cw.get('workoutSource'), 'text-bg-secondary', 'workoutSource'],
      [hasGpsTrack(cw) ? 'GPS track' : null, 'text-bg-info', 'locationSamples'],
      [workout?.get('isAllStrength') ? 'Dryland' : null, 'text-bg-dark', 'Workout_v3.isAllStrength'],
      [cw.get('isGuided') ? 'Guided' : 'Unguided', 'text-bg-light border', 'isGuided'],
      [cw.get('isGenerated') ? 'Generated' : null, 'text-bg-primary', 'isGenerated'],
      [workout?.get('isTestSetWorkout') ? 'Test set' : null, 'text-bg-warning', 'Workout_v3.isTestSetWorkout'],
      [cw.get('isSplashModeRunner') ? 'Splash mode' : null, 'text-bg-info', 'isSplashModeRunner'],
      [cw.get('isDisqualifiedFromChallenges') ? 'Challenge ineligible' : null, 'text-bg-danger', 'isDisqualifiedFromChallenges']
    ].filter(([label]) => label)
    return badges.map(([label, className, source]) => (
      <span key={label} className={'badge me-1 ' + className} title={source}>{label}</span>
    ))
  }

  renderHeader = () => {
    const cw = this.state.completedWorkout
    const user = cw.get('user')
    const userInfo = user?.get('userInfo_v3')
    const name = [userInfo?.get('firstName'), userInfo?.get('lastName')]
      .filter(Boolean).join(' ')
    return (
      <div className="mb-3">
        <h1 className="mb-1">{cw.get('title') || 'Untitled workout'}</h1>
        <div className="text-muted">
          {user
            ? <a href={'/#/members/' + user.id}>{name || user.id}</a>
            : 'No member'}
          {' · '}
          {formatSwimmerLocalTime(cw.get('dateCompleted'), cw.get('secondsFromGMT')) !== EMPTY
            ? formatSwimmerLocalTime(cw.get('dateCompleted'), cw.get('secondsFromGMT'))
            : formatDateTime(cw.get('dateCompleted'))}
          {' · '}
          <code>{cw.id}</code>
        </div>
        <div className="mt-2">{this.renderBadges()}</div>
      </div>
    )
  }

  renderSummary = () => {
    const cw = this.state.completedWorkout
    const hr = cw.get('hrSummary_v1')
    const distanceUnit = isYardsWorkout(cw) ? 'yd' : 'm'
    const laps = cw.get('lapSamplesData') || []
    return (
      <DetailSectionComponent id="cw-summary" title="Summary" collapsible defaultOpen>
        <DetailGrid>
          {this.renderStat('Distance', formatNumber(cw.get('totalDistance'), 0), distanceUnit, 'totalDistance (display units)',
            <a href="" onClick={this.handleEditDistance}>Edit</a>)}
          {this.renderStat('Duration', formatDuration(cw.get('totalTime')), null, 'totalTime (seconds)')}
          {this.renderStat('Free pace', formatDuration(cw.get('avgFreePacePer100m_v2')), '/100m', 'avgFreePacePer100m_v2 (seconds per 100 meters, whatever the pool unit)')}
          {this.renderStat('Free SWOLF', formatNumber(cw.get('swolfFreestyleAverage_v5'), 1), null, 'swolfFreestyleAverage_v5 (normalised to 25m)')}
          {this.renderStat('Avg HR', formatNumber(hr?.avgBpm, 0), 'bpm', 'hrSummary_v1.avgBpm')}
          {this.renderStat('Max HR', formatNumber(hr?.maxBpm, 0), 'bpm', 'hrSummary_v1.maxBpm')}
          {this.renderStat('Calories', formatNumber(cw.get('calories'), 0), 'kcal', 'calories')}
          {this.renderStat('RPE', cw.get('rpeScore') ? String(cw.get('rpeScore')) : EMPTY, '/10', 'rpeScore (1-10; 0 or absent = unrated)')}
          {this.renderStat('Water temp', describeWaterTemperature(cw),
            typeof cw.get('averageWaterTemperatureInCelsius') === 'number' ? '°C' : null,
            'averageWaterTemperatureInCelsius (null = checked, nothing usable)')}
          {this.renderStat('Laps', formatNumber(laps.length, 0), null, 'lapSamplesData')}
        </DetailGrid>
      </DetailSectionComponent>
    )
  }

  // Open on load only for open water, where the map is the point.
  renderContext = () => {
    const cw = this.state.completedWorkout
    const workout = cw.get('workout')
    const startLocation = cw.get('startLocation')
    const locationSamples = cw.get('locationSamples') || []
    return (
      <DetailSectionComponent id="cw-context" title="When & where" collapsible
        defaultOpen={hasGpsTrack(cw)}
        subtitle={joinSummary([
          describePool(cw.get('pool')),
          hasGpsTrack(cw) && 'GPS track',
          describeWorkoutType(cw.get('workoutType'))
        ])}>
        {(hasOpened) => (
          <>
            <DetailGrid>
              <DetailFieldComponent label="Completed (your time)" source="dateCompleted"
                value={formatDateTime(cw.get('dateCompleted'))} />
              <DetailFieldComponent label="Completed (swimmer's time)" source="dateCompleted + secondsFromGMT"
                value={formatSwimmerLocalTime(cw.get('dateCompleted'), cw.get('secondsFromGMT'))} />
              <DetailFieldComponent label="Swimmer UTC offset" source="secondsFromGMT (app logs default to 0 when the device sent none)"
                value={cw.get('secondsFromGMT') !== undefined ? formatUtcOffset(cw.get('secondsFromGMT')) : null} />
              <DetailFieldComponent label="Recorded start" source="recordedStartDate (copied from the workout since 2026-09-24)"
                value={cw.get('recordedStartDate') ? formatDateTime(cw.get('recordedStartDate')) : null} />
              <DetailFieldComponent label="Workout start" source="Workout_v3.recordedStartDate"
                value={workout?.get('recordedStartDate') ? formatDateTime(workout.get('recordedStartDate')) : null} />
              <DetailFieldComponent label="Workout end" source="Workout_v3.recordedEndDate"
                value={workout?.get('recordedEndDate') ? formatDateTime(workout.get('recordedEndDate')) : null} />
              <DetailFieldComponent label="Pool" source="pool (canonical, meters)"
                value={describePool(cw.get('pool'))} />
              <DetailFieldComponent label="Pool course (legacy)" source="poolCourse"
                value={describePoolCourse(cw.get('poolCourse'))} />
              <DetailFieldComponent label="Workout type" source="workoutType"
                value={describeWorkoutType(cw.get('workoutType'))} />
              <DetailFieldComponent label="Start location" source="startLocation">
                {startLocation
                  ? (
                    <span>
                      ({startLocation.latitude}, {startLocation.longitude}){' '}
                      <a href={`https://www.google.com/maps?q=${startLocation.latitude},${startLocation.longitude}`}
                        target="_blank" rel="noopener noreferrer">Map</a>
                    </span>
                    )
                  : null}
              </DetailFieldComponent>
              <DetailFieldComponent label="GPS samples" source="locationSamples (a track means open water)"
                value={locationSamples.length > 0 ? formatNumber(locationSamples.length, 0) : null} />
            </DetailGrid>
            {/* Leaflet can't size itself inside a closed section. */}
            {hasOpened && locationSamples.length > 5 && (
              <div className="mt-3">
                <MapRouteComponent coordinates={locationSamples.map(sample => [sample.latitude, sample.longitude])} />
              </div>
            )}
          </>
        )}
      </DetailSectionComponent>
    )
  }

  renderSourceDevice = () => {
    const cw = this.state.completedWorkout
    return (
      <DetailSectionComponent id="cw-device" title="Source & device" collapsible
        subtitle={joinSummary([
          cw.get('modelName'),
          cw.get('workoutSource'),
          cw.get('appVersion') && 'app ' + cw.get('appVersion')
        ])}>
        <DetailGrid>
          <DetailFieldComponent label="Logged by" source="user"
            action={<a href="" onClick={this.handleEditUser}>Edit</a>}>
            {cw.get('user')
              ? <a href={'/#/members/' + cw.get('user').id}>{cw.get('user').id}</a>
              : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Source" source="workoutSource" value={cw.get('workoutSource')} />
          <DetailFieldComponent label="From Garmin" source="isSourceGarmin" value={formatBool(cw.get('isSourceGarmin'))} />
          <DetailFieldComponent label="Uploading device" source="modelName (a phone-relayed watch swim shows the phone)" value={cw.get('modelName')} />
          <DetailFieldComponent label="OS version" source="deviceVersion" value={cw.get('deviceVersion')} />
          <DetailFieldComponent label="App version" source="appVersion" value={cw.get('appVersion')} />
          <DetailFieldComponent label="Runner platform" source="workoutRunnerPlatform" value={cw.get('workoutRunnerPlatform')} />
          <DetailFieldComponent label="Guided" source="isGuided" value={formatBool(cw.get('isGuided'))} />
          <DetailFieldComponent label="AI generated" source="isGenerated" value={formatBool(cw.get('isGenerated'))} />
          <DetailFieldComponent label="Ran guided on this device" source="isSplashModeRunner" value={formatBool(cw.get('isSplashModeRunner'))} />
          <DetailFieldComponent label="Likes" source="likeArray" value={formatNumber((cw.get('likeArray') || []).length, 0)} />
          <DetailFieldComponent label="Comments (social)" source="commentArray" value={formatNumber((cw.get('commentArray') || []).length, 0)} />
          <DetailFieldComponent label="Created" source="createdAt" value={formatDateTime(cw.createdAt)} />
          <DetailFieldComponent label="Updated" source="updatedAt" value={formatDateTime(cw.updatedAt)} />
          <DetailFieldComponent label="Sync UUID" source="syncUUID" value={cw.get('syncUUID')} wide />
          <DetailFieldComponent label="HealthKit UUID" source="healthKitUUID" value={cw.get('healthKitUUID')} wide />
          <DetailFieldComponent label="Workout hash" source="workoutHash_v1" value={cw.get('workoutHash_v1')} wide />
          <DetailFieldComponent label="Universal link" wide>
            <a href={'https://myswimpro.com/applink/?feature=social&completedWorkoutId=' + cw.id}>
              {'https://myswimpro.com/applink/?feature=social&completedWorkoutId=' + cw.id}
            </a>
          </DetailFieldComponent>
        </DetailGrid>
        <div className="detail-subheading">Challenges</div>
        <DetailGrid>
          <DetailFieldComponent label="Challenge eligibility" source="isDisqualifiedFromChallenges"
            action={<a href="" onClick={this.handleDisqualify}>Flip</a>}
            value={cw.get('isDisqualifiedFromChallenges') ? 'Ineligible' : 'Eligible'} />
        </DetailGrid>
      </DetailSectionComponent>
    )
  }

  renderHeartRate = () => {
    const cw = this.state.completedWorkout
    const hr = cw.get('hrSummary_v1')
    const samples = cw.get('heartRateData') || []
    const hasCharts = samples.length > 0 || !!hr?.histogram
    return (
      <DetailSectionComponent id="cw-heart-rate" title="Heart rate" collapsible
        defaultOpen={hasCharts}
        subtitle={joinSummary([
          `${formatNumber(samples.length, 0)} sample${samples.length === 1 ? '' : 's'}`,
          hr?.unusable && 'summary unusable'
        ])}>
        {(hasOpened) => (
          <>
            <DetailGrid>
              <DetailFieldComponent label="Average" source="hrSummary_v1.avgBpm"
                value={hr?.avgBpm !== undefined ? hr.avgBpm + ' bpm' : null} />
              <DetailFieldComponent label="Max" source="hrSummary_v1.maxBpm"
                value={hr?.maxBpm !== undefined ? hr.maxBpm + ' bpm' : null} />
              <DetailFieldComponent label="Summary samples" source="hrSummary_v1.sampleCount"
                value={formatNumber(hr?.sampleCount, 0)} />
              <DetailFieldComponent label="Coverage" source="hrSummary_v1.coverageSeconds"
                value={hr?.coverageSeconds !== undefined ? formatDuration(hr.coverageSeconds) : null} />
              <DetailFieldComponent label="Summary computed" source="hrSummary_v1.computedAt"
                value={hr?.computedAt ? formatDateTime(hr.computedAt) : null} />
              <DetailFieldComponent label="Summary version" source="hrSummary_v1.version" value={hr?.version} />
              {hr?.unusable && (
                <DetailFieldComponent label="Unusable" source="hrSummary_v1.reason"
                  value={HR_UNUSABLE_REASONS[hr.reason] || hr.reason || 'yes'} />
              )}
            </DetailGrid>
            {hasOpened && hasCharts && (
              <div className="mt-3">
                <HeartRateChartsComponent samples={samples} summary={hr} />
              </div>
            )}
          </>
        )}
      </DetailSectionComponent>
    )
  }

  // Collapsed: a long swim's laps would fill the screen. The stroke counts in
  // the header still make a mis-detected stroke stand out.
  renderLaps = () => {
    const laps = this.state.completedWorkout.get('lapSamplesData') || []
    return (
      <DetailSectionComponent id="cw-laps" title="Laps" collapsible
        subtitle={laps.length === 0
          ? 'none'
          : joinSummary([
            `${laps.length} lap${laps.length === 1 ? '' : 's'}`,
            ...lapStrokeCounts(laps).map(([name, count]) => `${name} ${count}`)
          ])}>
        <p className="small text-muted">lapSamplesData</p>
        <LapTableComponent laps={laps} onEdit={this.handleEditLap} />
      </DetailSectionComponent>
    )
  }

  renderByStroke = (workout) => {
    const rows = byStrokeRows(workout.get('totalDistanceByStroke'), workout.get('totalDurationByStroke'))
    if (rows.length === 0) {
      return <p className="text-muted mb-0">No per-stroke totals</p>
    }
    return (
      <table className="table table-sm mb-0">
        <thead>
          <tr><th>Stroke</th><th>Distance</th><th>Duration</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.stroke}>
              <td>{row.label}</td>
              <td>{formatNumber(row.distance, 0)}</td>
              <td>{formatDuration(row.duration)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  renderWorkoutRecord = (workout) => {
    const trainingPlanId = workout.get('trainingPlanId')
    const wodId = workout.get('workoutOfTheDayId')
    return (
      <div>
        <DetailGrid>
          <DetailFieldComponent label="Workout_v3" source="workout">
            <a href={'/#/workouts/' + workout.id}>{workout.id}</a>
          </DetailFieldComponent>
          <DetailFieldComponent label="Planned distance" source="Workout_v3.totalDistance"
            value={formatNumber(workout.get('totalDistance'), 0)} />
          <DetailFieldComponent label="Planned time" source="Workout_v3.totalTime"
            value={workout.get('totalTime') !== undefined ? formatDuration(workout.get('totalTime')) : null} />
          <DetailFieldComponent label="Pool" source="Workout_v3.pool" value={describePool(workout.get('pool'))} />
          <DetailFieldComponent label="Pool length (legacy)" source="Workout_v3.poolLength / poolCourse"
            value={workout.get('poolLength') !== undefined
              ? `${workout.get('poolLength')} (${describePoolCourse(workout.get('poolCourse'))})`
              : null} />
          <DetailFieldComponent label="Skill level (1-3)" source="Workout_v3.skillLevel" value={workout.get('skillLevel')} />
          <DetailFieldComponent label="Workout type" source="Workout_v3.workoutType" value={describeWorkoutType(workout.get('workoutType'))} />
          <DetailFieldComponent label="Dryland only" source="Workout_v3.isAllStrength" value={formatBool(workout.get('isAllStrength'))} />
          <DetailFieldComponent label="Test set" source="Workout_v3.isTestSetWorkout" value={formatBool(workout.get('isTestSetWorkout'))} />
          <DetailFieldComponent label="Admin workout" source="Workout_v3.isAdminWorkout" value={formatBool(workout.get('isAdminWorkout'))} />
          <DetailFieldComponent label="Premium" source="Workout_v3.isPremium" value={formatBool(workout.get('isPremium'))} />
          <DetailFieldComponent label="Free" source="Workout_v3.isFree" value={formatBool(workout.get('isFree'))} />
          <DetailFieldComponent label="Training plan" source="Workout_v3.trainingPlanId">
            {trainingPlanId ? <a href={'/#/trainingplans/' + trainingPlanId}>{trainingPlanId}</a> : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Plan entry" source="Workout_v3.trainingPlanEntryId" value={workout.get('trainingPlanEntryId')} />
          <DetailFieldComponent label="Workout of the day" source="Workout_v3.workoutOfTheDayId">
            {wodId ? <a href={'/#/wod/' + wodId}>{wodId}</a> : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="TrainingPeaks workout" source="Workout_v3.trainingPeaksWorkoutId"
            value={workout.get('trainingPeaksWorkoutId')} />
          <DetailFieldComponent label="Author" source="Workout_v3.authorUser (user id string)">
            {workout.get('authorUser')
              ? <a href={'/#/members/' + workout.get('authorUser')}>{workout.get('authorUser')}</a>
              : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Last edited by" source="Workout_v3.lastEditUser" value={workout.get('lastEditUser')?.id} />
          <DetailFieldComponent label="Last caller" source="Workout_v3.lastCaller" value={workout.get('lastCaller')} />
          <DetailFieldComponent label="Image" source="Workout_v3.imageUrl / imageID">
            {workout.get('imageUrl')
              ? <a href={workout.get('imageUrl')} target="_blank" rel="noopener noreferrer">{workout.get('imageID') || 'Open'}</a>
              : workout.get('imageID')}
          </DetailFieldComponent>
          <DetailFieldComponent label="Workout comments" source="Workout_v3.comments" value={workout.get('comments')} wide />
        </DetailGrid>
        {(workout.get('isGenerated') || workout.get('aiModel')) && (
          <>
            <div className="detail-subheading">Generation request</div>
            <DetailGrid>
              <DetailFieldComponent label="Model" source="Workout_v3.aiModel" value={workout.get('aiModel')} />
              <DetailFieldComponent label="Generated" source="Workout_v3.generatedDate"
                value={workout.get('generatedDate') ? formatDateTime(workout.get('generatedDate')) : null} />
              <DetailFieldComponent label="For member" source="Workout_v3.userId" value={workout.get('userId')} />
              <DetailFieldComponent label="Requested distance" source="Workout_v3.requestedDistance"
                value={formatNumber(workout.get('requestedDistance'), 0)} />
              <DetailFieldComponent label="Requested strokes" source="Workout_v3.requestedStrokes"
                value={joinList((workout.get('requestedStrokes') || []).map(getStrokeString))} />
              <DetailFieldComponent label="Requested equipment" source="Workout_v3.requestedEquipment"
                value={joinList(workout.get('requestedEquipment'))} />
              <DetailFieldComponent label="Requested focus" source="Workout_v3.requestedFocus"
                value={workout.get('requestedFocus')} wide />
            </DetailGrid>
          </>
        )}
        <div className="detail-subheading" title="Distance in display units. Duration is recorded time, else the interval, else (a known backend bug) the distance read as seconds.">
          Totals by stroke
        </div>
        {this.renderByStroke(workout)}
      </div>
    )
  }

  renderSets = () => {
    const workout = this.state.completedWorkout.get('workout')
    if (!workout) {
      return (
        <DetailSectionComponent id="cw-sets" title="Sets" collapsible defaultOpen>
          <p className="text-muted mb-0">No Workout_v3 attached</p>
        </DetailSectionComponent>
      )
    }
    const setGroups = workout.get('setGroups') || []
    return (
      <DetailSectionComponent id="cw-sets" title="Sets" collapsible defaultOpen
        subtitle={`${setGroups.length} set groups`}>
        {setGroups.length > 0
          ? setGroups.map(this.renderSetGroup)
          : <p className="text-muted mb-0">No set groups</p>}
      </DetailSectionComponent>
    )
  }

  renderRecord = () => {
    const workout = this.state.completedWorkout.get('workout')
    if (!workout) {
      return null
    }
    return (
      <DetailSectionComponent id="cw-record" title="Workout record" collapsible
        subtitle={joinSummary([
          workout.get('isGenerated') &&
            'generated' + (workout.get('aiModel') ? ' by ' + workout.get('aiModel') : ''),
          workout.get('trainingPlanId') && 'training plan',
          workout.get('workoutOfTheDayId') && 'workout of the day',
          workout.get('isTestSetWorkout') && 'test set'
        ]) || workout.id}>
        {this.renderWorkoutRecord(workout)}
      </DetailSectionComponent>
    )
  }

  renderMemberFeedback = () => {
    const cw = this.state.completedWorkout
    const feedback = this.state.memberFeedback
    const surveyCount = Array.isArray(feedback) ? feedback.length : 0
    // Open when the member wrote something; a bare RPE is already in Summary.
    return (
      <DetailSectionComponent id="cw-member-feedback" title="Member feedback" collapsible
        defaultOpen={!!cw.get('comments')}
        subtitle={joinSummary([
          cw.get('rpeScore') && 'RPE ' + cw.get('rpeScore'),
          cw.get('comments') && 'comments',
          surveyCount > 0 && `${surveyCount} survey answer${surveyCount === 1 ? '' : 's'}`
        ]) || (feedback === null ? null : 'none')}>
        <DetailGrid>
          <DetailFieldComponent label="RPE" source="rpeScore" value={cw.get('rpeScore')} />
          <DetailFieldComponent label="Comments" source="comments" wide>
            {cw.get('comments') ? <span className="detail-pre-line">{cw.get('comments')}</span> : null}
          </DetailFieldComponent>
        </DetailGrid>
        <div className="detail-subheading">Post-workout survey (CompletedWorkoutFeedback)</div>
        {this.renderLoadState(feedback, 'No survey answers') || feedback.map((row) => (
          <DetailGrid key={row.id}>
            <DetailFieldComponent label="Workout length" source="workoutLength" value={row.get('workoutLength')} />
            <DetailFieldComponent label="Intervals" source="workoutInterval" value={row.get('workoutInterval')} />
            <DetailFieldComponent label="Star rating" source="starRating" value={row.get('starRating')} />
            <DetailFieldComponent label="Recommendation seen" source="recommendationSeen"
              value={row.get('recommendationSeen') !== undefined
                ? formatBool(row.get('recommendationSeen')) +
                  (row.get('recommendationSeenTimestamp') ? ' · ' + formatDateTime(row.get('recommendationSeenTimestamp')) : '')
                : null} />
            <DetailFieldComponent label="Submitted" source="createdAt" value={formatDateTime(row.createdAt)} />
            <DetailFieldComponent label="Notes" source="notes" value={row.get('notes')} wide />
          </DetailGrid>
        ))}
      </DetailSectionComponent>
    )
  }

  renderActionPlan = (actionPlan) => {
    if (!Array.isArray(actionPlan) || actionPlan.length === 0) {
      return null
    }
    return (
      <ol className="mb-0">
        {actionPlan.map((task, index) => (
          <li key={index}>
            {task.title || task.text || task.key || JSON.stringify(task)}
            {task.type && <small className="text-muted"> ({task.type})</small>}
            {task.completed && <span className="badge text-bg-success ms-1">done</span>}
          </li>
        ))}
      </ol>
    )
  }

  renderCoachFeedback = () => {
    const rows = this.state.coachFeedback
    const current = rows?.find((row) => !row.get('isExpired'))
    const expiredCount = rows ? rows.filter((row) => row.get('isExpired')).length : 0
    const adHoc = this.state.adHocAnalysis
    let status = null
    if (rows === undefined) status = 'couldn\'t load'
    else if (current) status = 'generated ' + formatDate(current.createdAt)
    else if (rows) status = 'none'
    return (
      <DetailSectionComponent id="cw-coach-feedback" title="AI coach feedback" collapsible
        subtitle={joinSummary([
          status,
          current?.get('improvementOpportunity'),
          expiredCount > 0 && `${expiredCount} expired in All fields`,
          this.state.coachConversations?.length > 0 &&
            `${this.state.coachConversations.length} Coach Agent conversation${this.state.coachConversations.length === 1 ? '' : 's'}`
        ])}
        actions={
          <button type="button" className="btn btn-sm btn-outline-secondary"
            onClick={this.generateAnalysis} disabled={this.state.isGeneratingAnalysis}>
            {this.state.isGeneratingAnalysis ? 'Asking...' : 'Ask the coach'}
          </button>
        }>
        {this.renderLoadState(rows, 'No feedback generated yet') || (current
          ? (
            <div>
              <SafeMarkdownComponent text={current.get('text')} />
              <DetailGrid>
                <DetailFieldComponent label="Improvement opportunity" source="improvementOpportunity"
                  value={current.get('improvementOpportunity')} />
                <DetailFieldComponent label="Generated" source="createdAt" value={formatDateTime(current.createdAt)} />
                <DetailFieldComponent label="Action plan" source="actionPlan" wide>
                  {this.renderActionPlan(current.get('actionPlan'))}
                </DetailFieldComponent>
              </DetailGrid>
              <details className="mt-2">
                <summary className="small">Prompts</summary>
                <p className="text-muted small detail-pre-line"><b>System:</b> {current.get('systemPrompt')}</p>
                <p className="text-muted small detail-pre-line"><b>User:</b> {current.get('userPrompt')}</p>
              </details>
            </div>
            )
          : <p className="text-muted mb-0">Only expired feedback</p>)}
        {adHoc && (
          <div className="mt-3 border-top pt-3">
            <div className="detail-label mb-1">Ad-hoc answer (not saved)</div>
            <p className="small text-muted mb-1">
              getAIWorkoutAnalysis picks &quot;similar workouts&quot; from the signed-in
              user&apos;s history, so comparisons here are against your swims, not the member&apos;s.
            </p>
            <SafeMarkdownComponent text={adHoc.text} />
            <details>
              <summary className="small">Prompts</summary>
              <p className="text-muted small detail-pre-line"><b>System:</b> {adHoc.systemPrompt}</p>
              <p className="text-muted small detail-pre-line"><b>User:</b> {adHoc.userPrompt}</p>
            </details>
          </div>
        )}
        <div className="detail-subheading">Coach Agent conversations about this swim</div>
        {this.renderLoadState(this.state.coachConversations, 'None') || (
          <ul className="mb-0">
            {this.state.coachConversations.map((conversation) => (
              <li key={conversation.id}>
                <a href={'/#/swimCoachConversations/' + conversation.id}>
                  {formatDateTime(conversation.get('lastMessageAt') || conversation.createdAt)}
                </a>
                {conversation.get('lockedAt') && <span className="badge text-bg-danger ms-1">Locked</span>}
              </li>
            ))}
          </ul>
        )}
      </DetailSectionComponent>
    )
  }

  renderFileButton = (file) => (
    <a href={file.url()} download className="btn btn-sm btn-secondary">Download</a>
  )

  renderIntegrations = () => {
    const cw = this.state.completedWorkout
    const stravaActivityId = cw.get('stravaActivityId')
    const attachmentSyncUUIDs = cw.get('attachmentSyncUUIDs') || []
    const attachmentSyncTypes = cw.get('attachmentSyncTypes') || {}
    const uploaded = this.state.attachmentTypes || []
    let missingText = 'missing'
    if (this.state.attachmentTypes === null) missingText = 'loading...'
    if (this.state.attachmentTypes === undefined) missingText = 'couldn\'t load'
    // Declared attachments first, then anything uploaded that wasn't declared.
    const attachmentRows = [
      ...attachmentSyncUUIDs.map((uuid) => ({
        uuid,
        declaredType: attachmentSyncTypes[uuid],
        upload: uploaded.find((attachment) => attachment.syncUUID === uuid)
      })),
      ...uploaded
        .filter((attachment) => !attachmentSyncUUIDs.includes(attachment.syncUUID))
        .map((attachment) => ({ uuid: attachment.syncUUID, upload: attachment, isExtra: true }))
    ]
    const missingCount = Array.isArray(this.state.attachmentTypes)
      ? attachmentRows.filter((row) => !row.upload).length
      : 0
    const scheduled = this.state.scheduledWorkouts
    return (
      <div className="row detail-section-group" id="cw-integrations">
        <div className="col-lg-6">
          <DetailSectionComponent title="FIT files & integrations" collapsible
            subtitle={joinSummary([
              cw.get('sourceFITFile') && 'input .fit',
              cw.get('createdFITFile') && 'output .fit',
              cw.get('garminActivityId') && 'Garmin',
              stravaActivityId && 'Strava'
            ]) || 'none'}>
            <DetailGrid>
              <DetailFieldComponent label="Input .fit" source="sourceFITFile">
                {cw.get('sourceFITFile') ? this.renderFileButton(cw.get('sourceFITFile')) : null}
              </DetailFieldComponent>
              <DetailFieldComponent label="Output .fit" source="createdFITFile">
                <span>
                  {cw.get('createdFITFile') && this.renderFileButton(cw.get('createdFITFile'))}{' '}
                  <button className="btn btn-sm btn-outline-secondary" onClick={this.handleGenerateFitFile}>
                    Generate
                  </button>
                </span>
              </DetailFieldComponent>
              <DetailFieldComponent label="Logs to external services" source="shouldLogToExternalServices"
                value={formatBool(cw.get('shouldLogToExternalServices'))} />
              <DetailFieldComponent label="Garmin activity" source="garminActivityId" value={cw.get('garminActivityId')} />
              <DetailFieldComponent label="Garmin Connect workout" source="garminConnectWorkoutId"
                value={cw.get('garminConnectWorkoutId')} />
              <DetailFieldComponent label="Strava activity" source="stravaActivityId">
                {stravaActivityId
                  ? (
                    <a href={'https://strava.com/activities/' + stravaActivityId} target="_blank" rel="noopener noreferrer">
                      {stravaActivityId}
                    </a>
                    )
                  : null}
              </DetailFieldComponent>
              <DetailFieldComponent label="Strava share image" source="stravaShareImageUrl">
                {cw.get('stravaShareImageUrl')
                  ? <a href={cw.get('stravaShareImageUrl')} target="_blank" rel="noopener noreferrer">Open</a>
                  : null}
              </DetailFieldComponent>
              <DetailFieldComponent label="Strava heal attempted" source="stravaHealAttempted"
                value={formatBool(cw.get('stravaHealAttempted'))} />
            </DetailGrid>
            <div className="mt-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={this.handleRePushToStrava}
                disabled={this.state.isRePushingToStrava}
              >
                {this.state.isRePushingToStrava ? 'Re-pushing...' : 'Re-push to Strava'}
              </button>
              {this.state.stravaRePushResult && (
                <p className={'mt-2 mb-0 ' + (this.state.stravaRePushResult.success ? 'text-success' : 'text-danger')}>
                  <small>{this.state.stravaRePushResult.message}</small>
                </p>
              )}
            </div>
          </DetailSectionComponent>
        </div>
        <div className="col-lg-6">
          <DetailSectionComponent title="Attachments" collapsible
            subtitle={
              <>
                {attachmentSyncUUIDs.length} declared · {uploaded.length} uploaded
                {missingCount > 0 && <span className="text-danger"> · {missingCount} missing</span>}
              </>
            }>
            {attachmentRows.length === 0
              ? <p className="text-muted mb-0">No attachments</p>
              : (
                <table className="table table-sm mb-0">
                  <thead>
                    <tr><th>Sync UUID</th><th>Type</th><th>Uploaded</th></tr>
                  </thead>
                  <tbody>
                    {attachmentRows.map((row) => (
                      <tr key={row.uuid}>
                        <td>
                          <code className="small">{row.uuid}</code>
                          {row.isExtra && <span className="badge text-bg-light border ms-1">undeclared</span>}
                        </td>
                        <td>{row.declaredType || row.upload?.type || EMPTY}</td>
                        <td>
                          {row.upload
                            ? (
                              <span>
                                {row.upload.url
                                  ? <a href={row.upload.url} target="_blank" rel="noopener noreferrer">
                                      {row.upload.type}{row.upload.extension ? '.' + row.upload.extension : ''}
                                    </a>
                                  : row.upload.type}
                                {row.upload.createdAt && <small className="text-muted"> · {formatDateTime(row.upload.createdAt)}</small>}
                              </span>
                              )
                            : <span className="text-danger">{missingText}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
          </DetailSectionComponent>
          {/* The image only loads once someone opens the section. */}
          <DetailSectionComponent title="Photo" collapsible
            subtitle={cw.get('image') ? 'attached' : 'none'}>
            {(hasOpened) => cw.get('image')
              ? hasOpened && <img className="img-fluid" alt="Workout" src={cw.get('image').url()} />
              : <p className="text-muted mb-0">None</p>}
          </DetailSectionComponent>
          <DetailSectionComponent title="Scheduled as" collapsible
            subtitle={Array.isArray(scheduled)
              ? (scheduled.length > 0 ? scheduled[0].get('date') : 'not a plan workout')
              : null}>
            {this.renderLoadState(this.state.scheduledWorkouts, 'Not a scheduled plan workout') || (
              <ul className="mb-0">
                {this.state.scheduledWorkouts.map((row) => (
                  <li key={row.id}>
                    {row.get('date')} · plan entry {row.get('trainingPlanEntryId') || EMPTY}
                  </li>
                ))}
              </ul>
            )}
          </DetailSectionComponent>
        </div>
      </div>
    )
  }

  renderRaw = () => {
    const cw = this.state.completedWorkout
    const workout = cw.get('workout')
    const legacy = LEGACY_FIELDS.filter(([key]) => !isBlank(cw.get(key)))
    const coachFeedback = this.state.coachFeedback || []
    const memberFeedback = this.state.memberFeedback || []
    return (
      <DetailSectionComponent id="cw-raw" title="All fields" collapsible
        subtitle={rawFieldsSummary([
          [cw, COMPLETED_WORKOUT_SHOWN_FIELDS],
          ...(workout ? [[workout, WORKOUT_SHOWN_FIELDS]] : []),
          ...coachFeedback.map((row) => [row, COACH_FEEDBACK_SHOWN_FIELDS]),
          ...memberFeedback.map((row) => [row, MEMBER_FEEDBACK_SHOWN_FIELDS])
        ])}>
        <RawFieldsComponent object={cw} shownKeys={COMPLETED_WORKOUT_SHOWN_FIELDS} />
        {workout && <RawFieldsComponent object={workout} shownKeys={WORKOUT_SHOWN_FIELDS} />}
        {coachFeedback.map((row) => (
          <RawFieldsComponent key={row.id} object={row} label={'WorkoutCoachFeedback' + (row.get('isExpired') ? ' (expired)' : '')}
            shownKeys={COACH_FEEDBACK_SHOWN_FIELDS} />
        ))}
        {memberFeedback.map((row) => (
          <RawFieldsComponent key={row.id} object={row} shownKeys={MEMBER_FEEDBACK_SHOWN_FIELDS} />
        ))}
        {legacy.length > 0 && (
          <DetailMore label="Legacy fields" count={legacy.length}>
            <DetailGrid>
              {legacy.map(([key, label]) => (
                <DetailFieldComponent key={key} label={label} source={key}
                  value={typeof cw.get(key) === 'number' ? formatNumber(cw.get(key), 2) : describeRawValue(key, cw.get(key))} />
              ))}
            </DetailGrid>
          </DetailMore>
        )}
      </DetailSectionComponent>
    )
  }

  renderLapModal = () => {
    if (!this.state.activeLap) {
      return null
    }
    return (
      <Modal
        isOpen={this.state.modalIsOpen}
        onRequestClose={this.closeModal}
        style={customStyles}
        contentLabel="Edit lap"
      >
        <form onSubmit={this.handleSaveLap}>
          <h5>Lap {this.state.activeLapIndex + 1}</h5>
          <div className="mb-3">
            <label htmlFor="lapStrokeType">Stroke type</label>
            <select
              className="form-select"
              id="lapStrokeType"
              value={this.state.activeLap.strokeType}
              name="strokeType"
              onChange={this.onLapChange}
            >
              {strokeList.map((item) => {
                return <option value={item.value} key={item.value}>{item.label}</option>
              })}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="lapTotalSwimTime">totalSwimTime</label>
            <input
              type="text"
              className="form-control"
              name="totalSwimTime"
              id="lapTotalSwimTime"
              onChange={this.onLapChange}
              value={this.state.activeLap.totalSwimTime}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="lapStrokeCount">strokeCount</label>
            <input
              type="text"
              className="form-control"
              name="strokeCount"
              id="lapStrokeCount"
              onChange={this.onLapChange}
              value={this.state.activeLap.strokeCount}
            />
          </div>
          <button type="submit" className="btn btn-primary">
            Save & Update
          </button>
        </form>
      </Modal>
    )
  }

  render = () => {
    if (!this.state.completedWorkout) return <div className="container py-4">Loading...</div>

    return (
      <div className="container py-4">
        {this.renderHeader()}
        <DetailJumpNavComponent sections={SECTIONS} />
        {this.renderSummary()}
        {this.renderHeartRate()}
        {/* The sets open on the left; the rest of the swim's context sits
            collapsed beside them, each header carrying a one-line summary. */}
        <div className="row">
          <div className="col-lg-6">{this.renderSets()}</div>
          <div className="col-lg-6">
            {this.renderContext()}
            {this.renderSourceDevice()}
            {this.renderRecord()}
            <div className="detail-section-group" id="cw-feedback">
              {this.renderMemberFeedback()}
              {this.renderCoachFeedback()}
            </div>
          </div>
        </div>
        {this.renderLaps()}
        {this.renderIntegrations()}
        {this.renderRaw()}
        {this.renderLapModal()}
      </div>
    )
  }
}

CompletedWorkout.propTypes = {
  id: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  })
}
